import React from 'react';
import { Box, Container, Typography, Chip } from '@mui/material';
import { styled } from '@mui/material/styles';
import StarIcon from '@mui/icons-material/Star';
import withWebsiteContent from '../../../common/withWebsiteContent';
import { sectionScrollMarginSx } from '../../../common/hooks/useScrollSpyHash';

const RatingBadge = styled(Box)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  padding: `${theme.spacing(1)} ${theme.spacing(2)}`,
  borderRadius: 12,
  fontWeight: 700,
  fontSize: '1.5rem',
}));

/**
 * Displays guest rating and review-derived themes (Booking.com, 9.8 Exceptional).
 */
export const GuestHighlightsPure = ({ id, title, rating, ratingLabel, source, themes, images = [] }) => {
  const hasContent = title || rating || (Array.isArray(themes) && themes.length > 0);
  if (!hasContent) return null;

  return (
    <Box
      id={id}
      component="section"
      sx={{
        ...sectionScrollMarginSx,
        py: 6,
        backgroundColor: 'grey.50',
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Container>
        {title && (
          <Typography
            variant="h4"
            component="h2"
            sx={{ textAlign: 'center', mb: 3, fontWeight: 600 }}
          >
            {title}
          </Typography>
        )}
        {(rating || ratingLabel) && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 3 }}>
            <RatingBadge>
              <StarIcon sx={{ fontSize: 28 }} />
              {rating}
            </RatingBadge>
            {ratingLabel && (
              <Chip
                label={ratingLabel}
                sx={{
                  fontWeight: 600,
                  backgroundColor: 'success.dark',
                  color: 'white',
                  fontSize: '0.95rem',
                }}
              />
            )}
            {source && (
              <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>
                {source}
              </Typography>
            )}
          </Box>
        )}
        {Array.isArray(themes) && themes.length > 0 && (
          <Box
            component="ul"
            sx={{
              m: 0,
              p: 0,
              listStyle: 'none',
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
              gap: 2,
              maxWidth: 900,
              mx: 'auto',
            }}
          >
            {themes.map((t, i) => (
              <Box
                component="li"
                key={i}
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1,
                  '&::before': {
                    content: '"✓"',
                    color: 'success.main',
                    fontWeight: 700,
                    flexShrink: 0,
                  },
                }}
              >
                <Typography variant="body1" color="text.secondary">
                  {t}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </Container>
    </Box>
  );
};

const GuestHighlights = withWebsiteContent(GuestHighlightsPure);
GuestHighlights.displayName = 'GuestHighlights';

export default GuestHighlights;
